import { api } from './client';
import type { DoctorGroup } from '@/lib/doctor-groups';
import { i18nArray } from '@/lib/i18n-map';

/**
 * Ce tipărește o chitanță despre clinică — `GET /settings/receipt`.
 *
 * Nu `ClinicSettings`: acelea cer `setari.vede`, pe care nici recepția, nici
 * medicii nu-l au, deci chitanța tipărită de ei ieșea doar cu numele clinicii.
 * Ruta asta cere `incasari.vede` — exact cine are butonul de chitanță — iar
 * sigla vine deja semnată.
 */
export interface ReceiptClinic {
  name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  vat: string | null;
  iban: string | null;
  bankName: string | null;
  receiptFooter: string | null;
  /** Link semnat, valabil o oră. */
  logoUrl: string | null;
}

export interface ClinicSettings {
  name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  vat: string | null;
  logo: string | null;
  /** Read-only: these three come from the tenant registry, not this screen. */
  currency: string;
  language: string;
  plan: string;
  /** Contul pentru plăți prin transfer, tipărit pe chitanță. */
  iban: string | null;
  bankName: string | null;
  /** Rândul de sub totalul chitanței; gol înseamnă textul implicit. */
  receiptFooter: string | null;
  /** Datele juridice, pentru documente (SET-3). */
  legalName: string | null;
  licenseNo: string | null;
  directorName: string | null;
  legalRep: string | null;
  website: string | null;
  workStart: string;
  workEnd: string;
  /** Zilele săptămânii în care se lucrează, ISO: 1 = luni … 7 = duminică. */
  workDays: number[];
  notifSms: boolean;
  notifEmail: boolean;
  reminderHours: number;
  reminderChannel: string;
  reminderTemplate: string | null;
  paymentConfig: Record<string, unknown>;
  stockConfig: Record<string, unknown>;
  financeConfig: Record<string, unknown>;
  orgConfig: Record<string, unknown>;
  updatedAt: string;
}

/**
 * Written out rather than derived from `ClinicSettings`: the API validates with
 * `forbidNonWhitelisted`, so sending back `currency`, `plan` or `updatedAt`
 * would fail the whole request.
 */
/** Ce a configurat platforma pentru clinică, în config/tenants.yaml (SET-7). */
export interface PlatformInfo {
  slug: string;
  primaryDomain: string;
  domains: string[];
  timezone: string;
  locale: string;
  currency: string;
  plan: string;
  primaryColor: string | null;
  mailFromName: string;
  mailFrom: string;
  mailReplyTo: string | null;
  smsFrom: string | null;
  smsOwnSender: boolean;
}

export interface UpdateClinicSettingsInput {
  name?: string;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  vat?: string | null;
  logo?: string | null;
  iban?: string | null;
  bankName?: string | null;
  receiptFooter?: string | null;
  legalName?: string | null;
  licenseNo?: string | null;
  directorName?: string | null;
  legalRep?: string | null;
  website?: string | null;
  workStart?: string;
  workEnd?: string;
  workDays?: number[];
  notifSms?: boolean;
  notifEmail?: boolean;
  reminderHours?: number;
  reminderChannel?: string;
  reminderTemplate?: string | null;
}

export interface NotificationChannelMeta {
  id: string;
  label: string;
  /**
   * Poate canalul trimite ceva, acum, pentru clinica asta? Nu se poate bifa
   * altfel. De ce nu poate e treaba platformei, nu a clinicii.
   */
  available?: boolean;
}

export interface NotificationRuleMeta {
  id: string;
  label: string;
  trigger: string;
  channels: string[];
  template: string;
  /** True when something actually sends this rule; see the note on the screen. */
  dispatched: boolean;
}

export interface NotificationSettings {
  channels: Record<string, { enabled: boolean }>;
  rules: Record<string, { enabled: boolean; channels: string[]; template: string }>;
  catalog: { channels: NotificationChannelMeta[]; rules: NotificationRuleMeta[] };
  /** Pleacă ceva singur pe instalarea asta? Fals = toate automatizările sunt oprite. */
  automated: boolean;
}

export interface UpdateNotificationSettingsInput {
  channels: Record<string, { enabled: boolean }>;
  rules: Record<string, { enabled: boolean; channels: string[]; template?: string | null }>;
}

export interface PaymentTemplateInput {
  id: string;
  name: string;
  type: 'monthly' | 'per_visit' | 'onetime';
  advancePct: number;
  installments?: number;
  dueDay?: number;
  discountPct?: number;
}

export interface PaymentConfigInput {
  templates: PaymentTemplateInput[];
  reminderLeadDays: number;
  rounding: number;
  defaultDueDay: number;
}

/** `GET /settings/mine` — each slice present only for the key that manages it. */
export interface MySettings {
  updatedAt: string;
  stockConfig?: Record<string, unknown>;
  paymentConfig?: Record<string, unknown>;
  financeLock?: Record<string, unknown> | null;
  hr?: Record<string, unknown> | null;
}

export const settingsApi = {
  clinic: () => api.get<ClinicSettings>('/settings/clinic'),
  /**
   * Ce din setări administrează omul ăsta — stocul (asistenta), planurile de
   * plată și blocajul (contabilul), regulile de personal (HR). Fiecare felie
   * vine doar pentru cheia care o administrează; cine are `setari.vede` le
   * primește pe toate.
   */
  mine: () => api.get<MySettings>('/settings/mine'),
  platform: () => api.get<PlatformInfo>('/settings/platform'),
  receipt: () => api.get<ReceiptClinic>('/settings/receipt'),
  /**
   * Textul rechemării și numele și telefonul clinicii, sub `programari.vede` —
   * pentru Rechemare și Goluri în program, pe care le sună recepția.
   */
  outreach: () =>
    api.get<{
      clinicName: string;
      clinicPhone: string;
      recallTemplate: string;
      recallMonths: number;
    }>('/settings/outreach'),
  /** Numele, semnul și culoarea statusurilor, pentru oricine lucrează în clinică. */
  /**
   * Ce vede orice membru al echipei, fără `setari.vede`: cuvintele și culorile
   * statusurilor, sigla, tema, stilurile de desen și după câte minute se
   * blochează ecranul.
   */
  look: () =>
    api.get<{
      statusLabels: Record<string, unknown>;
      logo: string | null;
      workStart: string;
      workEnd: string;
      appearance: string;
      calStyle: string;
      chartStyle: string;
      /** Setul de iconițe al barei laterale; gol = „clasic". */
      iconSet: string;
      /** Stilul barei: „chip pe toate" (gol/implicit), „doar activ", „doar iconițe". */
      menuStyle: string;
      calLift: boolean;
      /** Pulsul de atenție de pe cardurile calendarului; pornit dacă nu e oprit explicit. */
      calPulse: boolean;
      /** Câte pulsuri dă o serie (1–3), viteza fiecăruia și cât de des se repetă seria. */
      calPulseCount: number;
      calPulseSpeed: 'lent' | 'normal' | 'rapid';
      calPulseCycle: number;
      /** Evidențierea cardului abia creat (inel + „NOU"); pornită dacă nu e oprită. */
      calNewHighlight: boolean;
      /** „Mod calm": mai puțină mișcare în toată aplicația; oprit implicit. */
      calmMode: boolean;
      /** După câte minute un pacient neajuns cere atenție (pulsul roșu). */
      calOverdueMin: number;
      /** Prima zi a săptămânii: 1 = luni (implicit), 0 = duminică. */
      weekStart: 0 | 1;
      /** Cuvintele care marchează un blocaj; gol = setul implicit. */
      blockKeywords: string[];
      /** Vederea implicită a calendarului pentru un cont nou. */
      calDefaultView: 'day' | 'week';
      /** Stilul de suprapunere implicit pentru un cont nou. */
      calDefaultOverlap: 'cascada' | 'coloane';
      /** Densitatea implicită a calendarului pentru un cont nou. */
      calDefaultDensity: 'fit' | 'scroll';
      /** Ecranul de pornire după autentificare. */
      landingScreen: 'dashboard' | 'calendar';
      /** Câte programări viitoare listează Panoul. */
      dashUpcomingCount: number;
      /** Grupele de medici ale clinicii, pentru filtrul calendarului. */
      doctorGroups: DoctorGroup[];
      autofillWaitlist: boolean;
      stockCategories: string[];
      lockAfterMin: number | null;
      /**
       * Aranjarea implicită a Panoului, aleasă de administrator în „Cabinete
       * personale": pe rol (`doctor`, `assistant`…) și, mai îngust, pe medic
       * anume (`byStaff[staffId]`). Ce lipsește pornește de la implicitul din cod.
       */
      cabinets: {
        admin?: { kpis: string[]; sections: string[] };
        doctor?: { kpis: string[]; sections: string[] };
        assistant?: { kpis: string[]; sections: string[] };
        reception?: { kpis: string[]; sections: string[] };
        byStaff?: Record<string, { kpis: string[]; sections: string[] }>;
      };
    }>('/settings/look'),

  /** Zilele în care clinica nu deschide, pe un an. */
  closedDays: (year: number) => api.get<ClosedDay[]>('/settings/closed-days', { query: { year } }),
  addClosedDay: (date: string, reason?: string | null) =>
    api.post<{ ok: boolean }>('/settings/closed-days', { date, reason }),
  removeClosedDay: (date: string) => api.delete<{ ok: boolean }>(`/settings/closed-days/${date}`),

  updateClinic: (body: UpdateClinicSettingsInput, ifVersion?: string) =>
    api.put<ClinicSettings>('/settings/clinic', { ...body, ifVersion }),
  /**
   * The one config the API validates field by field — see the DTO. Sent flat
   * rather than wrapped in `config`, which is what that endpoint expects.
   */
  updatePayment: (body: PaymentConfigInput, ifVersion?: string) =>
    api.put<ClinicSettings | MySettings>('/settings/payment', { ...body, ifVersion }),
  /**
   * `ifVersion` is `clinic.updatedAt`, read moments before the call: the
   * server refuses the write with 409 if the row moved on since, instead of
   * merging against a value the caller never saw. Optional — a caller that
   * skips it still merges, exactly as before this existed.
   */
  updateStock: (config: Record<string, unknown>, ifVersion?: string) =>
    api.put<ClinicSettings | MySettings>('/settings/stock', { config, ifVersion }),
  updateFinance: (config: Record<string, unknown>, ifVersion?: string) =>
    api.put<ClinicSettings>('/settings/finance', { config, ifVersion }),
  /** Rename one expense category where it stands — it keeps its code (contabilitate §2.7). */
  renameExpenseCategory: (body: { from: string; to: string }) =>
    api.post<ClinicSettings>('/settings/expense-categories/rename', body),
  updateOrg: (config: Record<string, unknown>, ifVersion?: string) =>
    api.put<ClinicSettings>('/settings/org', { config, ifVersion }),
  /** Blocajul perioadei, pe ușa contabilului (`financiar.blocheaza`). */
  updateFinanceLock: (financeLock: Record<string, unknown>) =>
    api.put<MySettings>('/settings/finance-lock', { config: { financeLock } }),
  /** Regulile de personal, pe ușa HR (`personal.editeaza`). */
  updateHrDefaults: (hr: Record<string, unknown>) =>
    api.put<MySettings>('/settings/hr-defaults', { config: { hr } }),
  /**
   * Grupele de medici, pe ușa lor: cere `programari.vede` sau `setari.modifica`,
   * deci le poate salva și recepția din calendar, nu doar adminul din Setări.
   * Întoarce doar grupele (cine editează din calendar n-are `setari.vede`).
   */
  updateDoctorGroups: (doctorGroups: DoctorGroup[]) =>
    api.put<{ doctorGroups: DoctorGroup[] }>('/settings/doctor-groups', { doctorGroups }),
  notifications: () => api.get<NotificationSettings>('/settings/notifications'),
  updateNotifications: (body: UpdateNotificationSettingsInput) =>
    api.put<NotificationSettings>('/settings/notifications', body),
  /** Setări → Personalizare → Module. Cere `module.gestioneaza`. */
  modules: () => api.get<ModulesSettings>('/settings/modules'),
  updateModules: (body: UpdateModulesInput) => api.put<ModulesSettings>('/settings/modules', body),
  /**
   * Un profil de mărime, aplicat o dată.
   *
   * Scrie aceleași comutatoare pe care le scrie `updateModules`, deci nu e o
   * setare nouă: după aplicare nimic nu ține minte profilul, iar ecranul arată
   * modulele una câte una, ca până acum.
   */
  applyProfile: (profile: ProfileKey, ifVersion?: string) =>
    api.put<ModulesSettings>('/settings/modules/profile', { profile, ifVersion }),
  /** Setări → Personalizare → Specialități. Cere `module.gestioneaza`. */
  specialties: () => api.get<SpecialtiesSettings>('/settings/specialties'),
  updateSpecialties: (specialties: Record<string, boolean>, ifVersion?: string) =>
    api.put<SpecialtiesSettings>('/settings/specialties', { specialties, ifVersion }),
};

/** O secțiune din meniu, cum o arată ecranul de module. */
export interface ModuleSetting {
  key: string;
  label: string;
  path: string | null;
  description: string;
  /** Mereu pornit: nu are comutator. */
  fixed: boolean;
  /** Supliment al contractului (Contabilitatea): se arată doar citit, îl schimbă platforma. */
  addon: boolean;
  /** În contractul clinicii; ce nu e inclus nu se poate porni de aici. */
  included: boolean;
  enabled: boolean;
  /** Ce are clinica în modul, spus înainte de oprire. */
  inUse: string | null;
}

export type ProfileKey = 'cabinet' | 'clinica' | 'retea';

/** Un profil de mărime, cu ce s-ar schimba dacă s-ar aplica acum. */
export interface ModuleProfile {
  key: ProfileKey;
  label: string;
  size: string;
  description: string;
  offLabels: string[];
  /** Ce nu poate decide un profil — pentru o rețea, filialele. */
  next: string[];
  turnsOn: string[];
  turnsOff: string[];
  /** Ce ar opri și are deja ceva în el. */
  inUse: string[];
  /** Ce ar porni și nu e în abonament, deci rămâne oprit. */
  locked: string[];
}

/** O specialitate, cum o arată ecranul. */
export interface SpecialtySetting {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
  /** Ce are clinica în ea — spus înainte de a o stinge. */
  inUse: string | null;
}

export interface SpecialtiesSettings {
  specialties: SpecialtySetting[];
  /** Clinica demo: se văd, nu se schimbă. */
  locked: boolean;
  updatedAt: string | null;
}

export interface ModulesSettings {
  modules: ModuleSetting[];
  /** Clinica demo: se vede, nu se schimbă. */
  locked: boolean;
  updatedAt: string | null;
  profiles: ModuleProfile[];
  /** Profilul care i se potrivește clinicii, și numerele din care s-a dedus. */
  suggested: { profile: ProfileKey; doctors: number; branches: number } | null;
}

/** Oglinda lui `UpdateModulesDto`: doar modulele care se pot opri. */
export interface UpdateModulesInput {
  modules: Partial<
    Record<
      | 'inventory'
      | 'imaging'
      | 'imagingReport'
      | 'teamChat'
      | 'economy'
      | 'reports'
      | 'hr'
      | 'dentalLab'
      | 'patientPortal'
      | 'waitlist'
      | 'orthodontics'
      | 'pedodontics',
      boolean
    >
  >;
  ifVersion?: string;
}

/** O zi în care clinica nu deschide. */
export interface ClosedDay {
  date: string;
  reason: string | null;
}

/** Zilele săptămânii, ISO, cum le arată ecranul. */
export const WEEKDAYS = i18nArray<{ iso: number; short: string; label: string }>('common', [
  { iso: 1, short: 'L', label: 'luni' },
  { iso: 2, short: 'Ma', label: 'marti' },
  { iso: 3, short: 'Mi', label: 'miercuri' },
  { iso: 4, short: 'J', label: 'joi' },
  { iso: 5, short: 'V', label: 'vineri' },
  { iso: 6, short: 'S', label: 'sambata' },
  { iso: 7, short: 'D', label: 'duminica' },
]);
